// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 42.2.3 E1 — Unified export format types for the multi-format
 * ExportService. Distinct from the legacy `ExportRecord` (Sprint 1.x)
 * and `EvidenceExportFormat` / `RegulatorExportFormat` per-domain enums
 * which describe entity-specific export records, not the generic
 * file format the ExportService produces.
 *
 * The 6 supported formats: `pdf`, `csv`, `docx`, `xlsx`, `zip`, `json`.
 * `zip` was added in Sprint 42.2.3 / 43.2.0 for evidence-pack bundles;
 * `json` was added in Sprint 43.5.5 for machine-readable exports.
 * Backend `ExportService` maps each to its format-specific builder;
 * frontend `ExportFormatMenu` shows only the formats a page declares.
 */
/**
 * Sprint 64.45.0 — the vocabulary as a VALUE, beside the type. Idea-catalog #100.
 *
 * A TypeScript union is erased at run time. A consumer that needs to iterate
 * the members, validate against them, or offer them has three choices:
 * transcribe them, reach for an adjacent structure that happens to enumerate
 * them, or write its own copy of whatever helper a peer wrote. All three drift,
 * and only the first is obvious in review.
 *
 * Measured: the union was **derived** in 6 places (`Object.keys` over one of
 * the `Record` maps below) and **transcribed** in several hundred.
 * `ExportFormatMenu` itself does the right thing — it imports the type and the
 * label map — and every one of its 145 call sites hands it a hand-written array
 * anyway, because there was no array to hand it.
 *
 * `as const` with the type derived as `(typeof X)[number]` is the authoring
 * shape 62.7.0 used for `QUALITY_ISSUE_STATUSES` and
 * `RELEASE_READINESS_OUTCOMES`. One shape, not two.
 */
export const UNIFIED_EXPORT_FORMATS = ['pdf', 'csv', 'docx', 'xlsx', 'zip', 'json'];
/**
 * The four formats renderable **from a definition**.
 *
 * A ZIP is an assembly and JSON is not rendered from sections, so
 * `export-service.ts` has always excluded them — but only at the type level, as
 * `Exclude<UnifiedExportFormat, 'zip' | 'json'>`. The concept *"the renderable
 * formats"* existed exactly once, in a type, and nowhere as a value; that is
 * why 105 call sites spell it out by hand.
 *
 * Derived by subtraction rather than re-listed, so a seventh format added to
 * `UNIFIED_EXPORT_FORMATS` appears here automatically and a reviewer has to
 * decide whether it belongs in `NON_RENDERABLE_EXPORT_FORMATS` instead. A
 * second hand-written list of four is the defect this constant is for.
 */
export const NON_RENDERABLE_EXPORT_FORMATS = ['zip', 'json'];
/**
 * ## The order is the SHIPPED order, and it is not the union's
 *
 * This array is written out rather than derived by `.filter`, and the reason is
 * a UX one that a tidier derivation would have shipped over.
 *
 * `UNIFIED_EXPORT_FORMATS` is in union order — `pdf, csv, docx, xlsx` — and
 * `ExportFormatMenu` renders its `formats` prop **in the order given**. All 108
 * call sites that offer the four renderable formats pass them as
 * `['csv', 'xlsx', 'pdf', 'docx']`, so deriving this by subtraction would have
 * silently reordered every export menu in the product, from CSV-first to
 * PDF-first, in the name of a shorter constant.
 *
 * That is 62.0.8's recorded precedent exactly: its ten modals read literally as
 * *"migrate these to the workspace pattern"*, and doing so would have shipped a
 * UX regression to make a suite green.
 *
 * So **membership is derived and asserted; order is a product decision and is
 * stated.** `export-format-vocabulary-64.45.0.test.ts` asserts this array's SET
 * equals `UNIFIED_EXPORT_FORMATS` minus `NON_RENDERABLE_EXPORT_FORMATS`, so a
 * seventh format cannot be forgotten here — but it does not constrain the
 * order, because the order is not a fact about the vocabulary.
 */
export const UNIFIED_EXPORT_RENDERABLE_FORMATS = ['csv', 'xlsx', 'pdf', 'docx'];
/**
 * Compile-time proof that the array above is exactly the renderable subset —
 * every member is renderable, and every renderable format is a member. The
 * run-time set-equality assertion in the test file is the same claim; this one
 * fails at `tsc` rather than at `vitest`, and neither is redundant: this cannot
 * catch a duplicate entry, and the test cannot catch a member that is not in
 * the union at all.
 */
const _renderableIsExact = UNIFIED_EXPORT_RENDERABLE_FORMATS;
const _renderableIsComplete = null;
void _renderableIsExact;
void _renderableIsComplete;
export const UNIFIED_EXPORT_FORMAT_MIME = {
    pdf: 'application/pdf',
    csv: 'text/csv',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    zip: 'application/zip',
    json: 'application/json',
};
export const UNIFIED_EXPORT_FORMAT_EXTENSION = {
    pdf: 'pdf',
    csv: 'csv',
    docx: 'docx',
    xlsx: 'xlsx',
    zip: 'zip',
    json: 'json',
};
export const UNIFIED_EXPORT_FORMAT_LABEL = {
    pdf: 'PDF',
    csv: 'CSV',
    docx: 'Word (DOCX)',
    xlsx: 'Excel (XLSX)',
    zip: 'ZIP archive',
    json: 'JSON',
};
//# sourceMappingURL=export-format.js.map